import React, { useState, useMemo, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Stack,
  Switch,
  Divider,
  TextField,
  Alert,
  Skeleton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Collapse,
  Tooltip,
  alpha,
  Autocomplete,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Delete,
  Add,
  VisibilityOutlined,
  AttachMoney,
  SyncAlt,
  EditOutlined,
  PublicOutlined,
  LocalOfferOutlined,
  ReceiptLongOutlined,
  MonetizationOnOutlined,
  GavelOutlined,
  AutoAwesomeOutlined,
  AccountBalanceOutlined,
  TimerOutlined,
  InfoOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { Theme } from '@mui/material/styles';
import { getAuthHeaders, makeApiCall } from '../utils/apiConfig';
import { apiService } from '../services/api';
import BulkPriceOverrideAction from '../components/BulkPriceOverrideAction';
import { useStorePlatform } from '../hooks/useStorePlatform';
import { platformLabel } from '../utils/storePlatform';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { BrandSelect, ConfirmDialog, NumberField, PageContainer, PageHeader, SaveBar } from '../components/ui';
import { parseNumberInput } from '../components/ui/NumberField';
import { useToast } from '../contexts/ToastContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { tokens } from '../theme/tokens';

/** Order-independent structural equality for the preferences document. */
const samePrefs = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || !a || !b) return false;
  const ao = a as Record<string, unknown>;
  const bo = b as Record<string, unknown>;
  const keys = Array.from(new Set(Object.keys(ao).concat(Object.keys(bo))));
  for (const k of keys) {
    if (ao[k] === undefined && bo[k] === undefined) continue;
    if (!samePrefs(ao[k], bo[k])) return false;
  }
  return true;
};

/** Small uppercase faint label above a mobile card's value, matching the ResponsiveRows mobile cell label. */
const mobileLabelSx = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase' as const,
  letterSpacing: '.06em',
  color: tokens.ink.faint,
  display: 'block',
  mb: '4px',
};

interface PricingPreferences {
  id?: string;
  organizationId: string;
  showMapPricing: boolean;
  showMsrp: boolean;
  showRebates: boolean;
  showTradeCredits: boolean;
  allowBelowMap: boolean;
  belowMapRequiresApproval: boolean;
  autoApplyRebates: boolean;
  autoSyncPricing: boolean;
  syncFrequency: 'realtime' | 'hourly' | 'daily' | 'weekly' | 'manual';
  primaryRegion: 'US' | 'CA';
  brandSettings: {
    [brand: string]: {
      enabled: boolean;
      showRebates: boolean;
      allowBelowMap: boolean;
    };
  };
  updatedAt?: Date;
}

interface PriceOverride {
  id: string;
  productId: string;
  model: string;
  brand: string;
  overrideType: 'map' | 'msrp' | 'rebate' | 'custom';
  originalValue: number;
  overrideValue: number;
  reason?: string;
  createdBy: string;
  createdAt: Date;
  expiresAt?: Date;
}

/** Chip text for an override type. Acronyms stay capitalised, words are sentence case. */
const OVERRIDE_TYPE_LABELS: Record<string, string> = {
  map: 'MAP',
  msrp: 'MSRP',
  rebate: 'Rebate',
  custom: 'Custom',
};
const overrideTypeLabel = (type: string): string => OVERRIDE_TYPE_LABELS[type] || type;

const SectionHeader: React.FC<{ icon: React.ReactNode; title: string; description?: React.ReactNode; action?: React.ReactNode }> = ({ icon, title, description, action }) => (
  <Box sx={{ px: 2.5, py: 1.5, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ display: 'flex', color: 'text.secondary' }}>{icon}</Box>
        <Typography variant="subtitle2" sx={{ letterSpacing: 0.3 }}>{title}</Typography>
      </Box>
      {description && <Typography variant="caption" color="text.secondary" sx={{ ml: 3.5, display: 'block' }}>{description}</Typography>}
    </Box>
    {action}
  </Box>
);

interface ToggleRowProps {
  icon: React.ReactNode;
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  warning?: boolean;
  indent?: boolean;
  children?: React.ReactNode;
}

const ToggleRow: React.FC<ToggleRowProps> = ({ icon, label, description, checked, onChange, warning, indent, children }) => (
  <>
    <Box sx={{ display: 'flex', alignItems: 'center', py: 1.25, ...(indent ? { ml: 3.5, pl: 1, borderLeft: '2px solid', borderColor: 'primary.light' } : {}) }}>
      <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center', color: warning ? 'warning.main' : 'action.active' }}>{icon}</Box>
      <Box sx={{ flex: 1, mr: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, color: warning ? 'warning.dark' : 'text.primary' }}>{label}</Typography>
        {description && <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>{description}</Typography>}
      </Box>
      <Switch size="small" checked={checked} onChange={(e) => onChange(e.target.checked)} />
    </Box>
    {children && <Collapse in={checked}>{children}</Collapse>}
  </>
);

/** "Shopify", "Shopify and BigCommerce", "Shopify, BigCommerce and Lightspeed". */
const listLabels = (labels: string[]): string =>
  labels.length <= 1 ? labels[0] || '' : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;

const INTERVAL_PHRASE: Record<string, string> = {
  hourly: 'hourly',
  daily: 'daily',
  weekly: 'weekly',
  manual: 'on demand',
};

export const StorePricingPreferencesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobile = useIsMobile();
  const { impersonatedOrg } = useImpersonation();
  const orgScope = impersonatedOrg?.id ?? 'self';
  const storePlatform = useStorePlatform();
  const [preferences, setPreferences] = useState<Partial<PricingPreferences>>({});
  const { showToast } = useToast();
  const [belowMapConfirmOpen, setBelowMapConfirmOpen] = useState(false);
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [newOverride, setNewOverride] = useState<Partial<PriceOverride>>({});
  const [productSearchInput, setProductSearchInput] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<any>(null);

  // The page's "Automatic Pricing Sync" toggle is a store-level preference. Whether prices are
  // actually pushed is set per integration (`settings.syncPricing`), so read that to say what is
  // really missing instead of always telling the store to "configure an integration".
  const pricingSyncStatus = useMemo(() => {
    if (storePlatform.loading) return { kind: 'loading' as const };
    if (!storePlatform.hasIntegration) return { kind: 'none' as const };
    const on = storePlatform.integrations.filter((i) => i.settings?.syncPricing === true);
    const off = storePlatform.integrations.filter((i) => i.settings?.syncPricing === false);
    if (on.length > 0) {
      const intervals = Array.from(new Set(on.map((i) => INTERVAL_PHRASE[i.settings?.syncInterval ?? ''] || ''))).filter(Boolean);
      return {
        kind: 'on' as const,
        names: listLabels(Array.from(new Set(on.map((i) => platformLabel(i.platform))))),
        cadence: intervals.length === 1 ? intervals[0] : '',
      };
    }
    if (off.length > 0) {
      return { kind: 'off' as const, names: listLabels(Array.from(new Set(off.map((i) => platformLabel(i.platform))))) };
    }
    return { kind: 'unknown' as const };
  }, [storePlatform.loading, storePlatform.hasIntegration, storePlatform.integrations]);

  // The two value boxes keep what was typed ("12." mid-entry) beside the numeric state the API payload uses;
  // a value set from outside (product pick, override type) wins whenever the typed text no longer matches it.
  const [originalText, setOriginalText] = useState('');
  const [overrideText, setOverrideText] = useState('');
  const shownValue = (text: string, num: number | undefined) =>
    (parseNumberInput(text) ?? undefined) === num ? text : num == null ? '' : String(num);

  const { data: productSearchResults, isFetching: productSearchLoading } = useQuery({
    queryKey: ['product-search-override', productSearchInput],
    queryFn: () => apiService.searchProducts(productSearchInput, 20),
    enabled: productSearchInput.length >= 2,
    staleTime: 10 * 1000,
  });

  const productOptions = useMemo(() => {
    const raw = productSearchResults?.data;
    if (!Array.isArray(raw)) return [];
    return raw.map((p: any) => ({
      id: p.id,
      label: `${p.name || p.model || p.title || p.id}`,
      brand: p.brand || '',
      model: p.model || p.name || '',
      price: p.price ?? p.pricing?.map ?? p.pricing?.retail ?? 0,
      mapPrice: p.pricing?.map ?? p.pricingData?.mapPrice ?? 0,
      msrp: p.pricing?.msrp ?? p.pricing?.retail ?? 0,
    }));
  }, [productSearchResults]);

  const handleProductSelect = useCallback((_: any, value: any) => {
    setSelectedProduct(value);
    if (value) {
      const overrideType = (newOverride.overrideType || 'map') as string;
      const original = overrideType === 'msrp' ? value.msrp : value.mapPrice || value.price;
      setNewOverride(prev => ({
        ...prev,
        model: value.model,
        brand: value.brand,
        originalValue: original > 0 ? original : undefined,
      }));
    }
  }, [newOverride.overrideType]);

  const { data: savedPreferences, isLoading: preferencesLoading } = useQuery<PricingPreferences>({
    queryKey: ['pricing-preferences', orgScope],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/store/pricing-preferences', { headers });
      if (!response.ok) {
        return {
          organizationId: '',
          showMapPricing: true,
          showMsrp: true,
          showRebates: true,
          showTradeCredits: true,
          allowBelowMap: false,
          belowMapRequiresApproval: true,
          autoApplyRebates: true,
          autoSyncPricing: false,
          syncFrequency: 'daily' as const,
          primaryRegion: 'US' as const,
          brandSettings: {},
        };
      }
      const result = await response.json();
      return result.preferences || result;
    },
  });

  React.useEffect(() => {
    if (savedPreferences) {
      setPreferences(savedPreferences);
    }
  }, [savedPreferences]);

  const { data: overrides, isLoading: overridesLoading } = useQuery<PriceOverride[]>({
    queryKey: ['price-overrides', orgScope],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/store/price-overrides', { headers });
      if (!response.ok) return [];
      const result = await response.json();
      return result.overrides || [];
    },
  });

  const savePreferencesMutation = useMutation({
    mutationFn: async (prefs: Partial<PricingPreferences>) => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/store/pricing-preferences', {
        method: 'PUT',
        headers,
        body: JSON.stringify(prefs),
      });
      if (!response.ok) throw new Error('Failed to save preferences');
      return response.json();
    },
    onSuccess: () => {
      showToast('Pricing preferences saved', 'success');
      queryClient.invalidateQueries({ queryKey: ['pricing-preferences'] });
    },
    onError: () => {
      showToast('Could not save pricing preferences. Your changes are still here.', 'error');
    },
  });

  const createOverrideMutation = useMutation({
    mutationFn: async (override: Partial<PriceOverride>) => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/store/price-overrides', {
        method: 'POST',
        headers,
        body: JSON.stringify(override),
      });
      if (!response.ok) throw new Error('Failed to create override');
      return response.json();
    },
    onSuccess: () => {
      setOverrideDialogOpen(false);
      setNewOverride({});
      queryClient.invalidateQueries({ queryKey: ['price-overrides'] });
    },
  });

  const deleteOverrideMutation = useMutation({
    mutationFn: async (overrideId: string) => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall(`/store/price-overrides/${overrideId}`, {
        method: 'DELETE',
        headers,
      });
      if (!response.ok) throw new Error('Failed to delete override');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['price-overrides'] });
    },
  });

  const handlePref = (key: keyof PricingPreferences, value: any) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
  };

  const dirty = !!savedPreferences && !samePrefs(preferences, savedPreferences);

  const formatCurrency = (value?: number) => {
    if (value === undefined || value === null) return '—';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  };

  if (preferencesLoading) {
    return (
      <PageContainer>
        <Stack spacing={2.5}>
          <Skeleton variant="rounded" height={180} sx={{ borderRadius: 2.5 }} />
          <Skeleton variant="rounded" height={200} sx={{ borderRadius: 2.5 }} />
          <Skeleton variant="rounded" height={120} sx={{ borderRadius: 2.5 }} />
          <Skeleton variant="rounded" height={200} sx={{ borderRadius: 2.5 }} />
        </Stack>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Pricing preferences"
        description={`Configure how MAP pricing and rebates are displayed and synced to ${storePlatform.hasIntegration ? storePlatform.platformsLabel : 'your e-commerce platform'}.`}
      />

      <Stack spacing={2.5}>
        {/* Display Preferences */}
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          <SectionHeader
            icon={<VisibilityOutlined fontSize="small" />}
            title="Display preferences"
            description="Control which pricing data is visible on product pages"
          />
          <Box sx={{ px: 2.5, py: 0.5 }}>
            <ToggleRow
              icon={<LocalOfferOutlined fontSize="small" />}
              label="MAP pricing"
              description="Show Minimum Advertised Price on product listings"
              checked={preferences.showMapPricing ?? true}
              onChange={(v) => handlePref('showMapPricing', v)}
            />
            <Divider />
            <ToggleRow
              icon={<MonetizationOnOutlined fontSize="small" />}
              label="MSRP comparison"
              description="Display manufacturer suggested retail price for reference"
              checked={preferences.showMsrp ?? true}
              onChange={(v) => handlePref('showMsrp', v)}
            />
            <Divider />
            <ToggleRow
              icon={<ReceiptLongOutlined fontSize="small" />}
              label="Active rebates"
              description="Show current manufacturer rebate offers on eligible products"
              checked={preferences.showRebates ?? true}
              onChange={(v) => handlePref('showRebates', v)}
            />
            <Divider />
            <ToggleRow
              icon={<AccountBalanceOutlined fontSize="small" />}
              label="Trade credits"
              description="Display available trade-in credit values. Only products whose vendor sheet lists a trade credit show one, so you may see none yet."
              checked={preferences.showTradeCredits ?? true}
              onChange={(v) => handlePref('showTradeCredits', v)}
            />
          </Box>
        </Paper>

        {/* Pricing Behavior */}
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          <SectionHeader
            icon={<AttachMoney fontSize="small" />}
            title="Pricing behavior"
            description="Rules for how prices are calculated and enforced"
          />
          <Box sx={{ px: 2.5, py: 0.5 }}>
            <ToggleRow
              icon={<WarningAmberOutlined fontSize="small" />}
              label="Allow selling below MAP"
              description="Permit pricing below the Minimum Advertised Price — may violate vendor agreements"
              checked={preferences.allowBelowMap ?? false}
              onChange={(v) => {
                // Turning this on is a risky change; ask first. Turning it off needs no confirmation.
                if (v) setBelowMapConfirmOpen(true);
                else handlePref('allowBelowMap', false);
              }}
              warning
            >
              <Box sx={{ ml: 3.5, mb: 1 }}>
                <ToggleRow
                  icon={<GavelOutlined fontSize="small" />}
                  label="Require approval"
                  description="Below-MAP pricing requires manager approval before publishing"
                  checked={preferences.belowMapRequiresApproval ?? true}
                  onChange={(v) => handlePref('belowMapRequiresApproval', v)}
                  indent
                />
              </Box>
            </ToggleRow>
            <Divider />
            <ToggleRow
              icon={<AutoAwesomeOutlined fontSize="small" />}
              label="Auto-apply instant rebates"
              description="Automatically deduct active instant rebates from the displayed price"
              checked={preferences.autoApplyRebates ?? true}
              onChange={(v) => handlePref('autoApplyRebates', v)}
            />
            <Divider />
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5 }}>
              <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center', color: 'action.active' }}><PublicOutlined fontSize="small" /></Box>
              <Box sx={{ flex: 1, mr: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>Primary region</Typography>
                <Typography variant="caption" color="text.secondary">Currency and regional pricing rules</Typography>
              </Box>
              <FormControl size="small" sx={{ width: 200 }}>
                <Select
                  value={preferences.primaryRegion ?? 'US'}
                  onChange={(e) => handlePref('primaryRegion', e.target.value)}
                  sx={{ borderRadius: 1.5, fontSize: '0.85rem' }}
                >
                  <MenuItem value="US">United States (USD)</MenuItem>
                  <MenuItem value="CA">Canada (CAD)</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Paper>

        {/* Sync Settings */}
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          <SectionHeader
            icon={<SyncAlt fontSize="small" />}
            title="Pricing sync"
            description="Automatically push pricing updates to your connected store"
          />
          <Box sx={{ px: 2.5, py: 0.5 }}>
            <ToggleRow
              icon={<TimerOutlined fontSize="small" />}
              label="Automatic pricing sync"
              description="Store-level preference for scheduled MAP and rebate pushes. What each integration actually pushes is set by its own Pricing sync setting on the Integrations page."
              checked={preferences.autoSyncPricing ?? false}
              onChange={(v) => handlePref('autoSyncPricing', v)}
            >
              <Box sx={{ ml: 5, mb: 1.5, pl: 1, borderLeft: '2px solid', borderColor: 'primary.light' }}>
                <FormControl size="small" sx={{ width: 240 }}>
                  <InputLabel>Sync frequency</InputLabel>
                  <Select
                    value={preferences.syncFrequency ?? 'daily'}
                    onChange={(e) => handlePref('syncFrequency', e.target.value)}
                    label="Sync frequency"
                    sx={{ borderRadius: 1.5, fontSize: '0.85rem' }}
                  >
                    <MenuItem value="realtime">Real-time (enterprise)</MenuItem>
                    <MenuItem value="hourly">Hourly</MenuItem>
                    <MenuItem value="daily">Daily</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="manual">Manual only</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </ToggleRow>
          </Box>
          {(pricingSyncStatus.kind === 'none' || pricingSyncStatus.kind === 'off' || pricingSyncStatus.kind === 'on') && (
            <Box sx={{ px: 2.5, pb: 2 }}>
              <Alert
                severity={pricingSyncStatus.kind === 'on' ? 'success' : 'info'}
                icon={<InfoOutlined sx={{ fontSize: 18 }} />}
                sx={{ borderRadius: 1.5, '& .MuiAlert-message': { fontSize: '0.8rem' } }}
                action={
                  pricingSyncStatus.kind === 'on' ? undefined : (
                    <Button color="inherit" size="small" onClick={() => navigate('/app/integrations')}>
                      Open Integrations
                    </Button>
                  )
                }
              >
                {pricingSyncStatus.kind === 'none' && 'No e-commerce integration is connected yet. Connect one on the Integrations page to push pricing to your store.'}
                {pricingSyncStatus.kind === 'off' && `Your ${pricingSyncStatus.names} integration doesn't have Pricing sync enabled. Turn it on in the integration's Sync Behavior tab to push prices to your store.`}
                {pricingSyncStatus.kind === 'on' && `Your ${pricingSyncStatus.names} integration already syncs pricing${pricingSyncStatus.cadence ? ` ${pricingSyncStatus.cadence}` : ''} from its own Pricing sync setting. The toggle above is a separate store-level preference and does not turn that off.`}
              </Alert>
            </Box>
          )}
        </Paper>

        {/* Price Overrides */}
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          <SectionHeader
            icon={<EditOutlined fontSize="small" />}
            title="Price overrides"
            description={
              <>
                Custom prices that replace vendor MAP/MSRP for specific products. To change the SKU or barcode your store uses, go to{' '}
                <RouterLink to="/app/sku-overrides">SKU mapping</RouterLink>.
              </>
            }
            action={
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <BulkPriceOverrideAction />
              <Button
                size="small"
                startIcon={<Add />}
                onClick={() => setOverrideDialogOpen(true)}
                sx={{ fontWeight: 500, fontSize: '0.8rem' }}
              >
                Add override
              </Button>
              </Box>
            }
          />
          <Box sx={{ px: 2.5, py: 2 }}>
            {overridesLoading ? (
              <Skeleton variant="rounded" height={140} sx={{ borderRadius: 1.5 }} />
            ) : !overrides || overrides.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <EditOutlined sx={{ fontSize: 36, color: 'grey.300', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">No price overrides configured</Typography>
                <Typography variant="caption" color="text.disabled">
                  Add an override to set a custom price for a specific product
                </Typography>
              </Box>
            ) : isMobile ? (
              <Paper variant="outlined" sx={{ borderRadius: 1.5 }}>
                <Stack divider={<Divider />}>
                  {overrides.map((override) => (
                    <Box key={override.id} sx={{ p: 2 }}>
                      <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{override.model}</Typography>
                          <Typography variant="caption" color="text.disabled">{override.brand}</Typography>
                        </Box>
                        <Chip label={overrideTypeLabel(override.overrideType)} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.65rem', borderColor: 'grey.200', flexShrink: 0 }} />
                      </Stack>
                      <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                        <Box>
                          <Typography component="span" sx={mobileLabelSx}>Original</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through', fontSize: '0.8rem' }}>
                            {formatCurrency(override.originalValue)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography component="span" sx={mobileLabelSx}>Override</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main', fontSize: '0.85rem' }}>
                            {formatCurrency(override.overrideValue)}
                          </Typography>
                        </Box>
                      </Stack>
                      {override.reason && (
                        <Box sx={{ mt: 1 }}>
                          <Typography component="span" sx={mobileLabelSx}>Reason</Typography>
                          <Typography variant="caption" color="text.secondary">{override.reason}</Typography>
                        </Box>
                      )}
                      <Box sx={{ mt: 1 }}>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<Delete sx={{ fontSize: 16 }} />}
                          onClick={() => deleteOverrideMutation.mutate(override.id)}
                        >
                          Remove
                        </Button>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>Product</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>Type</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>Original</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>Override</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>Reason</TableCell>
                      <TableCell width={48} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {overrides.map((override) => (
                      <TableRow key={override.id} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{override.model}</Typography>
                          <Typography variant="caption" color="text.disabled">{override.brand}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={overrideTypeLabel(override.overrideType)} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.65rem', borderColor: 'grey.200' }} />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through', fontSize: '0.8rem' }}>
                            {formatCurrency(override.originalValue)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main', fontSize: '0.85rem' }}>
                            {formatCurrency(override.overrideValue)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">{override.reason || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Remove override">
                            <IconButton size="small" onClick={() => deleteOverrideMutation.mutate(override.id)} sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                              <Delete sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Paper>
      </Stack>

      <SaveBar
        dirty={dirty}
        message="Unsaved changes. Display, pricing and sync rules use them once saved."
        saving={savePreferencesMutation.isPending}
        onSave={() => savePreferencesMutation.mutate(preferences)}
        onDiscard={() => { if (savedPreferences) setPreferences(savedPreferences); }}
      />

      {/* Allow-below-MAP confirmation: declining leaves the toggle off */}
      <ConfirmDialog
        open={belowMapConfirmOpen}
        title="Allow selling below MAP?"
        message="Selling below the Minimum Advertised Price may violate your vendor MAP agreements, and vendors can restrict or stop your supply if they find violations. Only turn this on if you have confirmed it is allowed for the brands you carry."
        confirmLabel="Allow below MAP"
        confirmColor="warning"
        onConfirm={() => {
          handlePref('allowBelowMap', true);
          setBelowMapConfirmOpen(false);
        }}
        onClose={() => setBelowMapConfirmOpen(false)}
      />

      {/* Add Override Dialog */}
      <Dialog
        open={overrideDialogOpen}
        onClose={() => { setOverrideDialogOpen(false); setSelectedProduct(null); setProductSearchInput(''); }}
        maxWidth="sm"
        fullWidth
        fullScreen={fullScreenDialog}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <Box sx={{ px: 3, pt: 2.5, pb: 1 }}>
          <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>Add price override</Typography>
          <Typography variant="caption" color="text.secondary">Search for a product to override its vendor MAP/MSRP pricing</Typography>
        </Box>
        <DialogContent sx={{ px: 3 }}>
          <Stack spacing={2.5}>
            {/* Product search */}
            <Autocomplete
              size="small"
              freeSolo
              options={productOptions}
              getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
              value={selectedProduct}
              inputValue={productSearchInput}
              onInputChange={(_, value) => setProductSearchInput(value)}
              onChange={handleProductSelect}
              loading={productSearchLoading}
              filterOptions={(x) => x}
              noOptionsText={productSearchInput.length < 2 ? 'Type at least 2 characters to search' : 'No products found'}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start !important', gap: 0.25, py: '8px !important' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{option.label}</Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">{option.brand}</Typography>
                    {option.mapPrice > 0 && <Chip label={`MAP $${option.mapPrice.toFixed(2)}`} size="small" sx={{ height: 18, fontSize: '0.6rem' }} />}
                    {option.msrp > 0 && option.msrp !== option.mapPrice && <Chip label={`MSRP $${option.msrp.toFixed(2)}`} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />}
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search product"
                  placeholder="Type a model name, SKU, or product name..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {productSearchLoading ? <CircularProgress size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            {/* Brand dropdown */}
            <BrandSelect
              value={newOverride.brand || ''}
              onChange={(value) => setNewOverride(prev => ({ ...prev, brand: value }))}
            />

            {/* Selected product summary */}
            {selectedProduct && (
              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: (theme: Theme) => alpha(theme.palette.primary.main, 0.03) }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{selectedProduct.model}</Typography>
                    <Typography variant="caption" color="text.secondary">{selectedProduct.brand}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {selectedProduct.mapPrice > 0 && <Chip label={`MAP $${selectedProduct.mapPrice.toFixed(2)}`} size="small" color="primary" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />}
                    {selectedProduct.msrp > 0 && <Chip label={`MSRP $${selectedProduct.msrp.toFixed(2)}`} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />}
                  </Box>
                </Box>
              </Paper>
            )}

            <FormControl fullWidth size="small">
              <InputLabel>Override type</InputLabel>
              <Select
                value={newOverride.overrideType || 'map'}
                onChange={(e) => {
                  const type = e.target.value as string;
                  setNewOverride(prev => {
                    const original = type === 'msrp'
                      ? (selectedProduct?.msrp || 0)
                      : (selectedProduct?.mapPrice || selectedProduct?.price || 0);
                    return { ...prev, overrideType: type as any, originalValue: original > 0 ? original : prev.originalValue };
                  });
                }}
                label="Override type"
              >
                <MenuItem value="map">MAP price</MenuItem>
                <MenuItem value="msrp">MSRP</MenuItem>
                <MenuItem value="rebate">Rebate amount</MenuItem>
                <MenuItem value="custom">Custom price</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <NumberField
                label="Original value"
                size="small"
                value={shownValue(originalText, newOverride.originalValue)}
                onChange={(text) => {
                  setOriginalText(text);
                  setNewOverride(prev => ({ ...prev, originalValue: parseNumberInput(text) ?? undefined }));
                }}
                min={0}
                step={0.01}
                adornment="$"
                sx={{ flex: 1 }}
              />
              <NumberField
                label="Override value"
                size="small"
                value={shownValue(overrideText, newOverride.overrideValue)}
                onChange={(text) => {
                  setOverrideText(text);
                  setNewOverride(prev => ({ ...prev, overrideValue: parseNumberInput(text) ?? undefined }));
                }}
                min={0}
                step={0.01}
                adornment="$"
                sx={{ flex: 1 }}
              />
            </Box>
            <TextField
              label="Reason (optional)"
              size="small"
              value={newOverride.reason || ''}
              onChange={(e) => setNewOverride(prev => ({ ...prev, reason: e.target.value }))}
              multiline
              rows={2}
              fullWidth
              placeholder="e.g. Promotional pricing for holiday sale"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={() => { setOverrideDialogOpen(false); setSelectedProduct(null); setProductSearchInput(''); }} sx={{ color: 'text.secondary' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              createOverrideMutation.mutate(newOverride);
              setSelectedProduct(null);
              setProductSearchInput('');
            }}
            disabled={!newOverride.model || !newOverride.overrideValue}
            sx={{ px: 3 }}
          >
            Add override
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default StorePricingPreferencesPage;
